import React, { useState, useCallback, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  LinearProgress,
  Alert,
  TextField,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Autocomplete,
  IconButton
} from '@mui/material';
import { CloudUpload, CheckCircle, Error as ErrorIcon, Delete as DeleteIcon, Description as DescriptionIcon, Edit as EditIcon } from '@mui/icons-material';
import { TiptapEditor } from './TiptapEditor';
import { Tab, Tabs } from '@mui/material';
import { lessonPlanService } from '../services/lessonPlanService';
import { subjectService } from '../services/subjectService';
import type { LessonPlanFormData, LessonPlanFolder, Subject } from '../types';
import { FolderSelector } from './FolderSelector';
import { logger } from '../utils/logger';
import { useAppStore } from '../stores/useAppStore';
import { isAdminOrApprover } from '../utils/rbac';
import { getDropzoneAcceptConfig, MAX_FILE_SIZE, FILE_VALIDATION_ERRORS } from '../utils/fileValidation';


interface LessonPlanUploadProps {
  onUploadSuccess?: (lessonPlan: any) => void;
  onUploadError?: (error: string) => void;
  onClose?: () => void;
  defaultFolderId?: string | null;
  folders?: LessonPlanFolder[];
  initialFiles?: File[];
}

export const LessonPlanUpload: React.FC<LessonPlanUploadProps> = ({
  onUploadSuccess,
  onUploadError,
  onClose,
  defaultFolderId,
  folders = [],
  initialFiles = []
}) => {
  const { user } = useAppStore();
  const userRoles = user?.roles || [];
  const [files, setFiles] = useState<File[]>(initialFiles);
  const [titles, setTitles] = useState<{ [key: string]: string }>(() => {
    const t: { [key: string]: string } = {};
    initialFiles.forEach(f => {
      if (f.type !== 'text/html' && !f.name.endsWith('.html')) {
        t[f.name] = f.name.substring(0, f.name.lastIndexOf('.'));
      }
    });
    return t;
  });

  // Digital Mode State
  const [uploadMode, setUploadMode] = useState<'file' | 'digital'>('file');
  const [digitalContent, setDigitalContent] = useState('');
  const [digitalTitle, setDigitalTitle] = useState('');

  // Auto-save State
  const [showDraftAlert, setShowDraftAlert] = useState(false);
  const DRAFT_KEY_PREFIX = `lessonPlan_draft_${user?.id}`;

  // Check for draft on mount
  useEffect(() => {
    const savedDraft = localStorage.getItem(DRAFT_KEY_PREFIX);
    if (savedDraft) {
      try {
        const { content, title, timestamp } = JSON.parse(savedDraft);
        // Only show alert if there's actual content and it's less than 24h old
        if ((content || title) && (Date.now() - timestamp < 24 * 60 * 60 * 1000)) {
          setShowDraftAlert(true);
        }
      } catch (e) {
        console.error('Error parsing draft', e);
      }
    }
  }, [DRAFT_KEY_PREFIX]);

  // Auto-save effect
  useEffect(() => {
    if (uploadMode === 'digital' && (digitalContent || digitalTitle)) {
      const timer = setTimeout(() => {
        localStorage.setItem(DRAFT_KEY_PREFIX, JSON.stringify({
          content: digitalContent,
          title: digitalTitle,
          timestamp: Date.now()
        }));
      }, 2000); // Save after 2 seconds of no typing
      return () => clearTimeout(timer);
    }
  }, [digitalContent, digitalTitle, uploadMode, DRAFT_KEY_PREFIX]);

  const restoreDraft = () => {
    const savedDraft = localStorage.getItem(DRAFT_KEY_PREFIX);
    if (savedDraft) {
      try {
        const { content, title } = JSON.parse(savedDraft);
        setDigitalContent(content || '');
        setDigitalTitle(title || '');
        setUploadMode('digital');
        setShowDraftAlert(false);
      } catch (e) {
        console.error('Error restoring draft', e);
      }
    }
  };



  // Handle Initial Files (Template)
  useEffect(() => {
    if (initialFiles.length > 0) {
      const file = initialFiles[0];
      // Check if it's a "Content Template" (HTML file created by frontend)
      if (file.type === 'text/html' || file.name.endsWith('.html')) {
        setUploadMode('digital');
        setDigitalTitle(file.name.replace('.html', ''));
        const reader = new FileReader();
        reader.onload = (e) => {
          setDigitalContent(e.target?.result as string || '');
        };
        reader.readAsText(file);
        // Clear files from file-upload mode
        setFiles([]);
      } else {
        setUploadMode('file');
      }
    }
  }, [initialFiles]);
  const MAX_UPLOAD_LIMIT = parseInt(import.meta.env.VITE_LESSON_PLAN_UPLOAD_LIMIT || '10', 10);
  const [selectedSubject, setSelectedSubject] = useState<Subject | null>(null);
  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [loadingSubjects, setLoadingSubjects] = useState(false);
  const [subjectSearchTerm, setSubjectSearchTerm] = useState('');
  const [week, setWeek] = useState<number | ''>('');
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(defaultFolderId || null);
  const [submitImmediately, setSubmitImmediately] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Clear draft on successful upload
  useEffect(() => {
    if (success) {
      localStorage.removeItem(DRAFT_KEY_PREFIX);
    }
  }, [success, DRAFT_KEY_PREFIX]);
  const [warning, setWarning] = useState<string | null>(null);
  const [submissionPeriod, setSubmissionPeriod] = useState<{ startDate: string; endDate: string } | null>(null);
  const [overrideConfirmOpen, setOverrideConfirmOpen] = useState(false);
  const [overrideConfirmed, setOverrideConfirmed] = useState(false);

  // Load subjects for autocomplete
  const loadSubjects = async (search?: string): Promise<Subject[]> => {
    try {
      setLoadingSubjects(true);
      logger.log('📚 Loading subjects from database for "Môn học" dropdown...', search ? `(search: ${search})` : '');
      const result = await subjectService.getSubjects({
        page: 1,
        pageSize: 1000,
        search: search || ''
      });
      const loadedSubjects = result.subjects || [];
      logger.log('✅ Loaded subjects from database:', loadedSubjects.length, 'subjects');
      setSubjects(loadedSubjects);
      return loadedSubjects;
    } catch (err) {
      logger.error('❌ Error loading subjects:', err);
      return [];
    } finally {
      setLoadingSubjects(false);
    }
  };

  // Load subjects on mount
  useEffect(() => {
    loadSubjects();
  }, []);

  // Load subjects when search term changes
  useEffect(() => {
    const timer = setTimeout(() => {
      loadSubjects(subjectSearchTerm);
    }, 300);
    return () => clearTimeout(timer);
  }, [subjectSearchTerm]);

  // Update selectedFolderId when defaultFolderId changes
  React.useEffect(() => {
    setSelectedFolderId(defaultFolderId || null);
  }, [defaultFolderId]);

  // Load submission period
  useEffect(() => {
    lessonPlanService.getSubmissionPeriod().then(period => {
      if (period) {
        setSubmissionPeriod(period);
      }
    }).catch(err => {
      logger.error('Error loading submission period:', err);
    });
  }, []);

  const isOutsideSubmissionPeriod = useCallback((): boolean => {
    if (!submissionPeriod?.startDate || !submissionPeriod?.endDate) return false;
    const start = new Date(submissionPeriod.startDate);
    const end = new Date(submissionPeriod.endDate);
    if (isNaN(start.getTime()) || isNaN(end.getTime())) return false;
    const now = new Date();
    return now < start || now > end;
  }, [submissionPeriod]);

  const formatSubmissionPeriodLine = useCallback((): string => {
    if (!submissionPeriod?.startDate || !submissionPeriod?.endDate) return '';
    const start = new Date(submissionPeriod.startDate);
    const end = new Date(submissionPeriod.endDate);
    if (isNaN(start.getTime()) || isNaN(end.getTime())) return '';
    const startStr = start.toLocaleString('vi-VN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
    const endStr = end.toLocaleString('vi-VN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
    return `Thời gian nộp giáo án: Từ ${startStr} đến ${endStr}`;
  }, [submissionPeriod]);

  const handleFileSelect = useCallback((selectedFile: File) => {
    // Validate file
    const validation = lessonPlanService.validateFile(selectedFile);
    if (!validation.isValid) {
      setError(validation.error || 'File không hợp lệ');
      return;
    }

    if (files.length >= MAX_UPLOAD_LIMIT) {
      setError(`Chỉ được upload tối đa ${MAX_UPLOAD_LIMIT} giáo án cùng lúc`);
      return;
    }

    setFiles(prev => [...prev, selectedFile]);
    setError(null);

    // Auto-fill title from filename
    const fileName = selectedFile.name;
    const nameWithoutExtension = fileName.substring(0, fileName.lastIndexOf('.'));
    setTitles(prev => ({ ...prev, [selectedFile.name]: nameWithoutExtension }));
  }, [files.length, MAX_UPLOAD_LIMIT]);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = event.target.files;
    if (selectedFiles) {
      Array.from(selectedFiles).forEach(file => {
        if (files.length < MAX_UPLOAD_LIMIT) {
          handleFileSelect(file);
        }
      });
    }
  };

  const handleRemoveFile = (fileName: string) => {
    setFiles(prev => prev.filter(f => f.name !== fileName));
    setTitles(prev => {
      const newTitles = { ...prev };
      delete newTitles[fileName];
      return newTitles;
    });
  };

  const { getRootProps, getInputProps, isDragActive, isDragReject } = useDropzone({
    onDrop: (acceptedFiles, fileRejections) => {
      if (fileRejections.length > 0) {
        setError(`File không hợp lệ. ${FILE_VALIDATION_ERRORS.INVALID_TYPE}`);
        return;
      }
      if (acceptedFiles.length > 0) {
        if (files.length + acceptedFiles.length > MAX_UPLOAD_LIMIT) {
          setError(`Chỉ được upload tối đa ${MAX_UPLOAD_LIMIT} giáo án cùng lúc`);
          return;
        }
        const newFiles = [...files, ...acceptedFiles];
        setFiles(newFiles);
        const newTitles = { ...titles };
        acceptedFiles.forEach(file => {
          const fileName = file.name;
          const nameWithoutExtension = fileName.substring(0, fileName.lastIndexOf('.'));
          newTitles[file.name] = nameWithoutExtension;
        });
        setTitles(newTitles);
        setError(null);
      }
    },
    accept: getDropzoneAcceptConfig(),
    maxFiles: MAX_UPLOAD_LIMIT,
    maxSize: MAX_FILE_SIZE,
    multiple: true
  });

  const doSubmit = async () => {
    if (uploadMode === 'file' && files.length === 0) {
      setError('Vui lòng chọn ít nhất một file giáo án');
      return;
    }
    if (uploadMode === 'digital' && !digitalTitle) {
      setError('Vui lòng nhập tiêu đề giáo án');
      return;
    }

    setIsUploading(true);
    setError(null);
    setSuccess(null);
    setWarning(null);

    try {
      let uploadPromises;

      if (uploadMode === 'digital') {
        const formData: any = {
          title: digitalTitle.trim(),
          subjectId: selectedSubject?.id || null,
          week: week !== '' ? Number(week) : undefined,
          content: digitalContent,
          is_digital: true,
          folderId: selectedFolderId || undefined,
          submitImmediately
        };
        uploadPromises = [lessonPlanService.submitLessonPlan(formData)];
      } else {
        uploadPromises = files.map(async (file) => {
          const formData: LessonPlanFormData = {
            title: (titles[file.name] || file.name.substring(0, file.name.lastIndexOf('.'))).trim(),
            subjectId: selectedSubject?.id || null,
            week: week !== '' ? Number(week) : undefined,
            file: file,
            folderId: selectedFolderId || undefined,
            submitImmediately
          };
          return await lessonPlanService.submitLessonPlan(formData);
        });
      }

      const results = await Promise.all(uploadPromises);
      const pendingSubmitCount = results.filter(r => r?.status === 'PendingSubmit').length;
      const successCount = results.length;

      // Check for warnings (Admin/BGH submitting outside period)
      const warnings = results.filter(r => (r as any)?.warning).map(r => (r as any).warning);
      if (warnings.length > 0) {
        setWarning(warnings[0]); // Show first warning
      }

      if (pendingSubmitCount > 0) {
        setSuccess(`Đã lưu ${successCount} giáo án (${pendingSubmitCount} ở trạng thái "Chờ nộp"). Hệ thống sẽ tự nộp khi tới thời gian nộp giáo án.`);
      } else {
        setSuccess(submitImmediately ? `Nộp ${successCount} giáo án thành công!` : `Lưu ${successCount} giáo án thành công!`);
      }

      setFiles([]);
      setTitles({});
      setSelectedSubject(null);
      setSubjectSearchTerm('');
      setWeek('');
      setSubmitImmediately(false);

      if (onUploadSuccess && results.length > 0) {
        onUploadSuccess(results[0]); // Call with first result for backward compatibility
      }
    } catch (err) {
      logger.error('Error uploading lesson plan:', err);
      let errorMessage = (err as globalThis.Error)?.message || 'Có lỗi xảy ra khi nộp giáo án';

      // Parse error response if it contains period info
      try {
        if (err instanceof globalThis.Error && errorMessage.includes('Chưa đến hoặc đã hết thời gian')) {
          // Try to extract period from error message or parse JSON
          const errorText = errorMessage;
          if (errorText.includes('period') || errorText.includes('startDate')) {
            // Error already contains formatted message from backend
            errorMessage = errorText;
          } else {
            // Try to parse as JSON to get period
            const jsonMatch = errorText.match(/\{.*\}/);
            if (jsonMatch) {
              const errorData = JSON.parse(jsonMatch[0]);
              if (errorData.period) {
                const startDate = new Date(errorData.period.startDate);
                const endDate = new Date(errorData.period.endDate);
                const startStr = startDate.toLocaleString('vi-VN', {
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                });
                const endStr = endDate.toLocaleString('vi-VN', {
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                });
                errorMessage = `Chưa đến hoặc đã hết thời gian nộp giáo án. Vui lòng liên hệ người phụ trách.\n\nThời gian nộp giáo án:\nTừ: ${startStr}\nĐến: ${endStr}`;
              }
            }
          }
        }
      } catch (parseErr) {
        // If parsing fails, use original error message
        logger.warn('Error parsing error message:', parseErr);
      }

      setError(errorMessage);

      // If error is about submission period, close dialog immediately
      if (errorMessage.includes('Chưa đến hoặc đã hết thời gian nộp giáo án')) {
        if (onClose) {
          onClose();
        }
      }

      if (onUploadError) {
        onUploadError(errorMessage);
      }
    } finally {
      setIsUploading(false);
      setOverrideConfirmed(false);
    }
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (uploadMode === 'file') {
      if (files.length === 0) {
        setError('Vui lòng chọn ít nhất một file giáo án');
        return;
      }

      // Validate all files have titles
      const missingTitles = files.filter(f => !titles[f.name]?.trim());
      if (missingTitles.length > 0) {
        setError(`Vui lòng nhập tiêu đề cho ${missingTitles.length} giáo án`);
        return;
      }
    } else {
      if (!digitalTitle.trim()) {
        setError('Vui lòng nhập tiêu đề giáo án');
        return;
      }
    }

    // Admin/BGH có thể nộp ngoài thời gian, nhưng phải confirm trước
    if (
      submitImmediately &&
      isAdminOrApprover(userRoles) &&
      submissionPeriod &&
      isOutsideSubmissionPeriod() &&
      !overrideConfirmed
    ) {
      setOverrideConfirmOpen(true);
      return;
    }

    await doSubmit();
  };

  const handleReset = () => {
    setFiles([]);
    setTitles({});
    setSelectedSubject(null);
    setSubjectSearchTerm('');
    setWeek('');
    setSelectedFolderId(defaultFolderId || null);
    setError(null);
    setSuccess(null);
    setWarning(null);
  };

  return (
    <Card>
      <CardContent>
        <Typography variant="h5" component="h2" gutterBottom>
          {initialFiles.length > 0 && uploadMode === 'digital' ? 'Tạo Giáo Án Từ Mẫu' : 'Nộp Giáo Án Mới'}
        </Typography>

        <Tabs
          value={uploadMode}
          onChange={(_, val) => setUploadMode(val)}
          sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label="Tải lên File" value="file" icon={<CloudUpload />} iconPosition="start" />
          <Tab label="Soạn thảo trực tuyến" value="digital" icon={<EditIcon />} iconPosition="start" />
        </Tabs>

        {error && (
          <Alert
            severity="error"
            sx={{ mb: 2 }}
            onClose={() => setError(null)}
          >
            <Box>
              <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 1 }}>
                {error.split('\n\n')[0]}
              </Typography>
              {error.includes('\n\n') && (
                <Box sx={{ mt: 1, pl: 2, borderLeft: '3px solid', borderColor: 'error.main' }}>
                  {error.split('\n\n')[1].split('\n').map((line, index) => (
                    <Typography key={index} variant="body2" sx={{ mt: index > 0 ? 0.5 : 0 }}>
                      {line}
                    </Typography>
                  ))}
                </Box>
              )}
            </Box>
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            <CheckCircle sx={{ mr: 1 }} />
            {success}
          </Alert>
        )}

        {warning && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {warning}
          </Alert>
        )}

        {/* Override submission confirmation (Admin/BGH only, outside submission period) */}
        <Dialog
          open={overrideConfirmOpen}
          onClose={() => {
            if (!isUploading) {
              setOverrideConfirmOpen(false);
              setOverrideConfirmed(false);
            }
          }}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Xác nhận vượt quyền</DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ mb: 1 }}>
              Bạn đang vượt quyền để nộp giáo án. Bạn có chắc không?
            </DialogContentText>
            {!!formatSubmissionPeriodLine() && (
              <Typography variant="caption" color="text.secondary">
                {formatSubmissionPeriodLine()}
              </Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setOverrideConfirmOpen(false);
                setOverrideConfirmed(false);
              }}
              disabled={isUploading}
            >
              Hủy
            </Button>
            <Button
              variant="contained"
              color="warning"
              onClick={() => {
                setOverrideConfirmOpen(false);
                setOverrideConfirmed(true);
                doSubmit();
              }}
              disabled={isUploading}
            >
              Đồng ý nộp
            </Button>
          </DialogActions>
        </Dialog>

        <form onSubmit={handleSubmit}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>

            {/* Digital Mode Editor */}
            {uploadMode === 'digital' && (
              <Box>
                <TextField
                  fullWidth
                  label="Tiêu đề giáo án *"
                  value={digitalTitle}
                  onChange={(e) => setDigitalTitle(e.target.value)}
                  required
                  sx={{ mb: 2 }}
                />
                <InputLabel sx={{ mb: 1 }}>Nội dung giáo án</InputLabel>
                <Box sx={{ border: '1px solid #ccc', borderRadius: 1, '& .ck-editor__editable': { minHeight: '300px' } }}>
                  {showDraftAlert && (
                    <Alert
                      severity="info"
                      action={
                        <Button color="inherit" size="small" onClick={restoreDraft}>
                          KHÔI PHỤC
                        </Button>
                      }
                      onClose={() => setShowDraftAlert(false)}
                      sx={{ mb: 1 }}
                    >
                      Phát hiện bản nháp chưa lưu từ lần trước.
                    </Alert>
                  )}
                  <TiptapEditor
                    content={digitalContent}
                    onChange={(content) => setDigitalContent(content)}
                    placeholder="Nhập nội dung giáo án..."
                  />
                </Box>
              </Box>
            )}

            {/* File Upload Dropzone */}
            {uploadMode === 'file' && (
              <Box
                {...getRootProps()}
                sx={{
                  border: '2px dashed',
                  borderColor: isDragReject ? 'error.main' : isDragActive ? 'primary.main' : '#ccc',
                  borderRadius: 2,
                  p: 4,
                  textAlign: 'center',
                  cursor: 'pointer',
                  backgroundColor: isDragActive ? 'action.hover' : isDragReject ? 'error.lighter' : 'background.paper',
                  transition: 'all 0.2s ease-in-out',
                  minHeight: 200,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  '&:hover': {
                    borderColor: 'primary.main',
                    backgroundColor: 'action.hover'
                  }
                }}
              >
                <input {...getInputProps()} id="file-input" />

                <CloudUpload
                  sx={{
                    fontSize: 64,
                    color: isDragActive ? 'primary.main' : isDragReject ? 'error.main' : 'text.secondary',
                    mb: 2,
                    transition: 'all 0.2s ease-in-out',
                    transform: isDragActive ? 'scale(1.1)' : 'scale(1)'
                  }}
                />

                <Typography variant="h6" gutterBottom sx={{ fontWeight: isDragActive ? 600 : 400 }}>
                  {isDragActive
                    ? 'Thả file vào đây để tải lên...'
                    : isDragReject
                      ? 'File không hợp lệ! Vui lòng chọn file: PDF, Word, Excel, Ảnh, Video, Audio, Archive'
                      : files.length > 0
                        ? `Đã chọn ${files.length}/${MAX_UPLOAD_LIMIT} file`
                        : 'Kéo thả file vào đây hoặc click để chọn file'}
                </Typography>

                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {isDragActive
                    ? 'File sẽ được tải lên ngay khi bạn thả'
                    : `Hỗ trợ: PDF, Word, Excel, Ảnh, Video, Audio, Archive (tối đa ${MAX_FILE_SIZE / 1024 / 1024}MB mỗi file, tối đa ${MAX_UPLOAD_LIMIT} file cùng lúc)`}
                </Typography>
              </Box>
            )}

            {/* File list (Only in File Mode) */}
            {uploadMode === 'file' && files.length > 0 && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Typography variant="subtitle2" fontWeight="bold">
                  Danh sách file đã chọn ({files.length}/{MAX_UPLOAD_LIMIT}):
                </Typography>
                {files.map((file, index) => (
                  <Box key={file.name} sx={{ display: 'flex', gap: 1, alignItems: 'center', p: 1, bgcolor: 'background.default', borderRadius: 1 }}>
                    <TextField
                      fullWidth
                      size="small"
                      label={`Tiêu đề giáo án ${index + 1} *`}
                      value={titles[file.name] || ''}
                      onChange={(e) => setTitles(prev => ({ ...prev, [file.name]: e.target.value }))}
                      required
                      disabled={isUploading}
                      helperText={file.name}
                    />
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleRemoveFile(file.name)}
                      disabled={isUploading}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                ))}
              </Box>
            )}

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>

              <Autocomplete
                fullWidth
                options={subjects}
                getOptionLabel={(option) => option.name}
                value={selectedSubject}
                onChange={(_, newValue) => {
                  setSelectedSubject(newValue);
                  setSubjectSearchTerm(newValue?.name || '');
                }}
                onInputChange={(_, newInputValue) => {
                  setSubjectSearchTerm(newInputValue);
                }}
                inputValue={subjectSearchTerm}
                loading={loadingSubjects}
                disabled={isUploading}
                noOptionsText="Không tìm thấy môn học"
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Môn học"
                    placeholder="Tìm kiếm môn học..."
                    helperText="Chọn môn học từ danh sách (không thể tạo mới)"
                  />
                )}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                onOpen={() => {
                  // Load subjects when dropdown opens if not already loaded
                  if (subjects.length === 0) {
                    loadSubjects();
                  }
                }}
              />

              <FormControl fullWidth disabled={isUploading}>
                <InputLabel>Tuần</InputLabel>
                <Select
                  value={week}
                  onChange={(e) => setWeek(e.target.value as number | '')}
                  label="Tuần"
                >
                  <MenuItem value="">
                    <em>Không chọn</em>
                  </MenuItem>
                  {Array.from({ length: 40 }, (_, i) => i + 1).map((weekNum) => (
                    <MenuItem key={weekNum} value={weekNum}>
                      Tuần {weekNum}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {folders.length > 0 && (
              <FolderSelector
                folders={folders}
                value={selectedFolderId}
                onChange={setSelectedFolderId}
                label="Chọn folder (tùy chọn)"
                allowNone={true}
                noneLabel="Không có folder (folder gốc)"
              />
            )}

            <FormControlLabel
              control={
                <Checkbox
                  checked={submitImmediately}
                  onChange={(e) => setSubmitImmediately(e.target.checked)}
                  disabled={isUploading}
                />
              }
              label="Nộp luôn (nếu không tick sẽ lưu vào thư mục của bạn để nộp sau)"
            />

            {submitImmediately && submissionPeriod && (
              <Alert severity="info" sx={{ mt: 1 }}>
                <Typography variant="body2">
                  Thời gian nộp giáo án: Từ {new Date(submissionPeriod.startDate).toLocaleString('vi-VN', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })} đến {new Date(submissionPeriod.endDate).toLocaleString('vi-VN', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </Typography>
              </Alert>
            )}

            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              <Button
                variant="outlined"
                onClick={handleReset}
                disabled={isUploading}
              >
                Làm mới
              </Button>

              <Button
                type="submit"
                variant="contained"
                disabled={(uploadMode === 'file' && files.length === 0) || (uploadMode === 'file' && Object.values(titles).some(t => !t?.trim())) || (uploadMode === 'digital' && !digitalTitle.trim()) || isUploading}
                startIcon={isUploading ? <CircularProgress size={20} /> : <CloudUpload />}
              >
                {isUploading ? 'Đang nộp...' : (uploadMode === 'file' && files.length > 1) ? `Nộp ${files.length} giáo án` : 'Nộp giáo án'}
              </Button>
            </Box>
          </Box>

          {isUploading && (
            <Box sx={{ mt: 2 }}>
              <LinearProgress />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Đang xử lý file và lưu thông tin...
              </Typography>
            </Box>
          )}
        </form>
      </CardContent>
    </Card>
  );
};
